import * as React from "react";
import { Box, Link, Typography } from "@mui/material";

export default function SquarespaceWebsiteDoc({ onNavigate }) {
  return (
    <Box id="squarespace-website" sx={{ mb: 4 }}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Creating a Squarespace Website in Admin
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        We can build a Squarespace site for someone from the admin. Every
        Squarespace site requires a Squarespace Customer and a Squarespace Order.
        We attach these to an internal Subscription with the right provider
        (source) and metadata so the order shows in our systems and links to the
        Squarespace Reseller API entities.
      </Typography>

      <Typography variant="h6" fontWeight="600" sx={{ mt: 3, mb: 1 }}>
        Process overview
      </Typography>
      <Box component="ol" sx={{ pl: 2, "& li": { mb: 1 } }}>
        <li>
          <strong>Create the Squarespace Customer.</strong> Use Squarespace →
          Customers to create or find the customer. The customer ID is stored in
          contact <code>meta_data.squarespace_customer_id</code>.
        </li>
        <li>
          <strong>Create the Order.</strong> Use Squarespace → Orders or Submit
          Order to create the website order for that customer. The order creates
          a Squarespace subscription on their side.
        </li>
        <li>
          <strong>Attach to our Subscription.</strong> Create or use an
          internal Subscription with <code>source: "Squarespace"</code> and the
          correct plan (provider). Add the Squarespace IDs to{" "}
          <code>subscription.meta_data</code> so we can link to the Reseller API
          entities and show the order in internal systems.
        </li>
      </Box>

      <Typography variant="h6" fontWeight="600" sx={{ mt: 3, mb: 1 }}>
        Subscription meta_data fields (Squarespace)
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        These fields in <code>subscription.meta_data</code> link our internal
        subscription to Squarespace Reseller API entities. They are required for
        orders to show correctly and for portal cancellation to work.
      </Typography>
      <Box
        component="table"
        sx={{
          width: "100%",
          borderCollapse: "collapse",
          "& th, & td": {
            border: "1px solid",
            borderColor: "divider",
            px: 1.5,
            py: 1,
            textAlign: "left",
            fontSize: "0.875rem",
          },
          "& th": { bgcolor: "action.hover", fontWeight: 600 },
          "& code": { fontFamily: "monospace", fontSize: "0.85em" },
        }}
      >
        <thead>
          <tr>
            <th>Field</th>
            <th>Description</th>
            <th>Links to</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <code>squarespace_customer_id</code>
            </td>
            <td>Squarespace Reseller API customer ID</td>
            <td>SS Customers API</td>
          </tr>
          <tr>
            <td>
              <code>squarespace_order_id</code>
            </td>
            <td>Squarespace Reseller API order ID</td>
            <td>SS Orders API</td>
          </tr>
          <tr>
            <td>
              <code>squarespace_subscription_id</code>
            </td>
            <td>Squarespace Reseller API subscription ID (from the order)</td>
            <td>SS Subscriptions API; required for portal cancel</td>
          </tr>
          <tr>
            <td>
              <code>pricingId</code>
            </td>
            <td>Squarespace pricing ID (from subscription_plan.meta_data)</td>
            <td>SS Products/Prices API</td>
          </tr>
        </tbody>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }} paragraph>
        When an order is created via the payload process (Submit Order), these
        fields are auto-populated. For older or manually created subscriptions,
        add them via Edit meta_data on the Subscription Detail page.
      </Typography>

      <Typography variant="h6" fontWeight="600" sx={{ mt: 3, mb: 1 }}>
        Making an order show on internal systems
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        For an order to appear and be usable in our admin: the Subscription must
        have <code>source: "Squarespace"</code>, the correct plan with
        Squarespace vendor, and <code>meta_data</code> populated with{" "}
        <code>squarespace_customer_id</code>, <code>squarespace_order_id</code>,
        and ideally <code>squarespace_subscription_id</code>. The Provisioning tab
        on the Subscription Detail page shows status checks for these links.
      </Typography>

      {onNavigate && (
        <Box sx={{ mt: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Link
            component="button"
            variant="body2"
            onClick={() => onNavigate("/dashboard/squarespace/customers")}
          >
            Squarespace → Customers
          </Link>
          <Link
            component="button"
            variant="body2"
            onClick={() => onNavigate("/dashboard/squarespace/orders")}
          >
            Squarespace → Orders
          </Link>
          <Link
            component="button"
            variant="body2"
            onClick={() => onNavigate("/dashboard/squarespace/process-order")}
          >
            Submit Order
          </Link>
          <Link
            component="button"
            variant="body2"
            onClick={() => onNavigate("/dashboard/subscriptions")}
          >
            Subscriptions
          </Link>
        </Box>
      )}
    </Box>
  );
}
